import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { ChevronLeft, ChevronRight, Tag } from "lucide-react";

export default function BrandCarousel() {
  const settings = useSettings();
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const trackRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  useEffect(() => {
    api.get("/brands/homepage").then((r) => setBrands(r.data)).catch(() => setBrands([]));
  }, []);

  const updateArrows = () => {
    const el = trackRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateArrows();
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);
    return () => { el.removeEventListener("scroll", updateArrows); window.removeEventListener("resize", updateArrows); };
  }, [brands]);

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.85), behavior: "smooth" });
  };

  if (settings && settings.homepage_brands_enabled === false) return null;
  if (!brands.length) return null;
  const showCount = !settings || settings.homepage_brands_show_count !== false;

  return (
    <section data-testid="homepage-brand-block" className="mb-12">
      <h2 className="text-2xl font-bold mb-1 text-steel">Shop By Brand</h2>
      <p className="text-sm text-muted-foreground mb-5">Jump straight to your preferred manufacturer.</p>
      <div className="relative">
        {canLeft && (
          <button data-testid="brand-scroll-left" onClick={() => scrollBy(-1)} aria-label="Scroll left"
            className="absolute -left-3 top-1/2 -translate-y-1/2 z-10 grid place-items-center w-10 h-10 rounded-full bg-white shadow-md border hover:bg-accent hover:text-steel transition-colors">
            <ChevronLeft size={22} />
          </button>
        )}
        <div ref={trackRef}
          className="flex gap-4 overflow-x-auto scroll-smooth px-1 py-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          {brands.map((b) => (
            <button key={b.id} data-testid={`homepage-brand-${b.slug}`}
              onClick={() => navigate(`/brand/${encodeURIComponent(b.brand)}`)}
              className="group shrink-0 basis-[40%] sm:basis-[22%] md:basis-[15%] lg:basis-[calc((100%-2.75rem)/12)] flex flex-col items-center text-center">
              <span className="w-full aspect-square rounded-xl border bg-white grid place-items-center overflow-hidden p-2 group-hover:border-accent transition-colors">
                {b.image_url
                  ? <img src={b.image_url} alt={b.brand} className="w-full h-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  : <Tag size={22} className="text-slate-300" />}
              </span>
              <span className="mt-2 text-xs md:text-sm font-semibold text-steel line-clamp-2 leading-snug group-hover:text-accent transition-colors">{b.brand}</span>
              {showCount && <span className="text-[11px] text-muted-foreground">{b.count} products</span>}
            </button>
          ))}
        </div>
        {canRight && (
          <button data-testid="brand-scroll-right" onClick={() => scrollBy(1)} aria-label="Scroll right"
            className="absolute -right-3 top-1/2 -translate-y-1/2 z-10 grid place-items-center w-10 h-10 rounded-full bg-white shadow-md border hover:bg-accent hover:text-steel transition-colors">
            <ChevronRight size={22} />
          </button>
        )}
      </div>
    </section>
  );
}
